import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories, hotShows, newShows, shows } from '@/mocks/shows';
import type { Show } from '@/mocks/shows';

interface CategorySectionProps {
  categorySlug: string;
  categoryName: string;
}

function CategorySection({ categorySlug, categoryName }: CategorySectionProps) {
  const filtered = categorySlug === 'all' ? shows : shows.filter(s => s.categorySlug === categorySlug);
  const displayShows = filtered.slice(0, 6);

  return (
    <section className="px-4 md:px-8 py-10 md:py-14 max-w-[1280px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-1 h-5 md:h-6 rounded-full bg-primary-500"></div>
          <h2 className="text-lg md:text-xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
            {categoryName}
          </h2>
        </div>
        <Link
          to={`/category/${categorySlug}`}
          className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-400 transition-colors duration-200 whitespace-nowrap"
        >
          查看全部
          <i className="ri-arrow-right-s-line"></i>
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
        {displayShows.map((show) => (
          <Link
            key={show.id}
            to={`/detail/${show.id}`}
            className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200 transition-all duration-300 cursor-pointer"
            data-product-shop
          >
            <div className="relative aspect-[2/3] overflow-hidden">
              <img
                src={show.coverImage}
                alt={show.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                title={show.title}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent opacity-60"></div>
              <div className="absolute top-2 right-2">
                <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-background-950/70 text-primary-400 whitespace-nowrap">
                  {show.rating}
                </span>
              </div>
              <div className="absolute bottom-2 left-2">
                <span className="text-[10px] text-foreground-700 bg-background-950/50 px-1.5 py-0.5 rounded whitespace-nowrap">
                  {show.currentEpisode}
                </span>
              </div>
            </div>
            <div className="p-2.5">
              <h3 className="text-xs font-semibold text-foreground-950 group-hover:text-primary-400 transition-colors duration-200 line-clamp-1 whitespace-nowrap overflow-hidden text-ellipsis">
                {show.title}
              </h3>
              <p className="text-[11px] text-foreground-500 mt-0.5 line-clamp-1 whitespace-nowrap overflow-hidden text-ellipsis">
                {show.englishTitle}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function HotSection() {
  return (
    <section className="px-4 md:px-8 py-10 md:py-14 max-w-[1280px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-1 h-5 md:h-6 rounded-full bg-accent-500"></div>
          <h2 className="text-lg md:text-xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
            热门推荐
          </h2>
          <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-accent-500/20 text-accent-400 whitespace-nowrap">
            HOT
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
        {hotShows.map((show) => (
          <Link
            key={show.id}
            to={`/detail/${show.id}`}
            className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200 transition-all duration-300 cursor-pointer"
            data-product-shop
          >
            <div className="relative aspect-[2/3] overflow-hidden">
              <img
                src={show.coverImage}
                alt={show.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                title={show.title}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent opacity-50"></div>
              <div className="absolute top-2 left-2">
                <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-accent-500 text-background-950 whitespace-nowrap">
                  HOT
                </span>
              </div>
              <div className="absolute top-2 right-2">
                <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-background-950/70 text-primary-400 whitespace-nowrap">
                  {show.rating}
                </span>
              </div>
            </div>
            <div className="p-2.5">
              <h3 className="text-xs font-semibold text-foreground-950 group-hover:text-primary-400 transition-colors duration-200 line-clamp-1 whitespace-nowrap overflow-hidden text-ellipsis">
                {show.title}
              </h3>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-[10px] text-foreground-400 bg-secondary-500/10 px-1 py-0.5 rounded whitespace-nowrap">
                  {show.category}
                </span>
                <span className="text-[10px] text-foreground-400 whitespace-nowrap">{show.currentEpisode}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function FeatureBanner() {
  return (
    <section className="relative px-4 md:px-8 py-16 md:py-24">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Dark%20cinema%20theater%20interior%20with%20warm%20golden%20curtains%2C%20luxurious%20velvet%20seats%2C%20dramatic%20stage%20lighting%2C%20elegant%20atmosphere%2C%20cinematic%20mood&width=1600&height=400&seq=feature-banner-home&orientation=landscape"
          alt="影院级观影体验"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-background-50/85"></div>
      </div>
      <div className="relative z-10 max-w-[1280px] mx-auto text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
          免费高清 · 流畅播放 · 秒开看剧
        </h2>
        <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto mb-8 leading-relaxed">
          爱丫爱丫影院汇聚海量电视剧资源，最新国产剧、韩剧全集免费在线观看。高清画质不缓冲，极速秒开，为您打造影院级观影体验。
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/20">
              <i className="ri-4k-line text-lg text-primary-400"></i>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-foreground-800 whitespace-nowrap">高清画质</p>
              <p className="text-xs text-foreground-500">1080P / 4K</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/20">
              <i className="ri-speed-up-line text-lg text-accent-400"></i>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-foreground-800 whitespace-nowrap">极速秒开</p>
              <p className="text-xs text-foreground-500">无需等待</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/20">
              <i className="ri-smartphone-line text-lg text-primary-400"></i>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-foreground-800 whitespace-nowrap">多端支持</p>
              <p className="text-xs text-foreground-500">手机/电脑/平板</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/20">
              <i className="ri-shield-check-line text-lg text-accent-400"></i>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-foreground-800 whitespace-nowrap">完全免费</p>
              <p className="text-xs text-foreground-500">无需付费</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LatestSection() {
  return (
    <section className="px-4 md:px-8 py-10 md:py-14 max-w-[1280px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-1 h-5 md:h-6 rounded-full bg-primary-500"></div>
          <h2 className="text-lg md:text-xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
            最新更新
          </h2>
          <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-primary-500/20 text-primary-400 whitespace-nowrap">
            NEW
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {newShows.concat(hotShows.filter(s => !s.isNew)).slice(0, 6).map((show) => (
          <Link
            key={show.id}
            to={`/detail/${show.id}`}
            className="group flex gap-4 p-3 rounded-lg bg-background-100 hover:bg-background-200 transition-all duration-300 cursor-pointer"
            data-product-shop
          >
            <div className="relative w-20 md:w-24 flex-shrink-0 aspect-[2/3] rounded-md overflow-hidden">
              <img
                src={show.coverImage}
                alt={show.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                title={show.title}
              />
              {show.isNew && (
                <div className="absolute top-1 left-1">
                  <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-primary-500 text-background-950 whitespace-nowrap">
                    NEW
                  </span>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0 flex flex-col justify-center">
              <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-400 transition-colors duration-200 line-clamp-1 whitespace-nowrap overflow-hidden text-ellipsis">
                {show.title}
              </h3>
              <p className="text-xs text-foreground-500 mt-1 line-clamp-2">
                {show.description}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[10px] text-foreground-400 bg-secondary-500/10 px-1.5 py-0.5 rounded whitespace-nowrap">
                  {show.category}
                </span>
                <span className="text-[10px] text-foreground-400 whitespace-nowrap">{show.currentEpisode}</span>
                <span className="text-[10px] text-primary-400 whitespace-nowrap">★ {show.rating}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main className="min-h-screen bg-background-50">
      {/* SEO H1 - hidden from view but accessible */}
      <h1 className="sr-only">爱丫爱丫影院 - 电视剧大全全集免费在线观看 | 最新国产剧韩剧全集高清</h1>

      <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20dark%20theater%20stage%20with%20golden%20spotlights%2C%20elegant%20velvet%20curtains%2C%20warm%20dramatic%20lighting%2C%20movie%20premiere%20atmosphere%2C%20luxurious%20cinema%20interior%2C%20high%20contrast&width=1600&height=700&seq=hero-main-bg&orientation=landscape"
            alt="爱丫爱丫影院"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background-50"></div>
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-10">
          <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center bg-primary-500/90 rounded-2xl mb-6 backdrop-blur-sm">
            <i className="ri-movie-2-line text-2xl md:text-3xl text-background-950"></i>
          </div>
          <h2 className="text-3xl md:text-6xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
            爱丫爱丫影院
          </h2>
          <p className="text-base md:text-xl text-foreground-600 mb-2 max-w-2xl">
            电视剧大全，全集免费在线观看
          </p>
          <p className="text-sm md:text-base text-foreground-500 mb-8 max-w-xl">
            最新国产剧、韩剧全集高清不缓冲，流畅播放，秒开看剧
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/category/all"
              className="px-8 py-3 text-sm font-semibold rounded-lg bg-primary-500 text-background-950 hover:bg-primary-400 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              开始追剧
            </Link>
            <Link
              to="/about"
              className="px-8 py-3 text-sm font-semibold rounded-lg bg-foreground-50/10 backdrop-blur-sm text-foreground-700 hover:bg-foreground-50/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              了解更多
            </Link>
          </div>
          <div className="flex items-center gap-6 mt-8">
            {categories.slice(1, 7).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex flex-col items-center gap-1 group cursor-pointer"
              >
                <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-foreground-50/10 backdrop-blur-sm group-hover:bg-primary-500/30 transition-all duration-300">
                  <i className={`${cat.icon} text-foreground-600 group-hover:text-primary-400 text-sm md:text-base`}></i>
                </div>
                <span className="text-[11px] md:text-xs text-foreground-500 group-hover:text-foreground-700 transition-colors duration-200 whitespace-nowrap">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <HotSection />

      <CategorySection categorySlug="chinese" categoryName="国产剧精选" />
      <CategorySection categorySlug="korean" categoryName="韩剧精选" />

      <FeatureBanner />

      <LatestSection />

      {/* Schema.org structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "爱丫爱丫影院",
            "url": typeof window !== 'undefined' ? window.location.origin : '',
            "description": "爱丫爱丫影院 - 电视剧大全全集免费在线观看，最新国产剧、韩剧全集高清不缓冲，流畅播放，秒开看剧。",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://aiyaaiya.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })
        }}
      />
    </main>
  );
}